import { useSelector } from 'react-redux';

import { useQueryClient } from '@tanstack/react-query';
import { useRouter } from 'next/router';

import { trackInsight } from '@framework/appInsights';
import { ServerStateKeys } from '@framework/constants/serverStateKeys';
import { getCurrentUserPermissionsInsights } from '@framework/utils/insights';

import { getCurrentAppSection, getIsAttendeeView } from '@redux/selectors/AppSelector';

import useLoggedMemberMeetingRoleInsights from '@meetings/common/hooks/useLoggedMemberMeetingRoleInsights';
import useMeetingIdFromRoute from '@meetings/hooks/useMeetingIdFromRoute';

import { useRuntimeContext } from '@components/pages/other/RuntimeContext/useRuntimeContext';

import type { Member } from '@type/members';
import type { Room } from '@type/rooms';

import useIsMeetingDetailsView from './useIsMeetingDetailsView';

const useSendInsight = () => {
    const router = useRouter();
    const queryClient = useQueryClient();

    const activeRoom = queryClient.getQueryData<Room>([ServerStateKeys.Room, router.query.room]);
    const member = queryClient.getQueryData<Member>([
        ServerStateKeys.Member,
        { id: activeRoom?.memberUuid }
    ]);
    const meetingId = useMeetingIdFromRoute();
    const loggedMemberRoles = useLoggedMemberMeetingRoleInsights();

    const currentAppSection = useSelector(getCurrentAppSection);
    const isAttendeeView = useSelector(getIsAttendeeView);

    const currentPermission = getCurrentUserPermissionsInsights(member);
    const { isMeetingDetailsView } = useIsMeetingDetailsView(currentAppSection);

    const { APPLICATION_INSIGHTS_ENABLED, MIXPANEL_ENABLED } = useRuntimeContext();

    const includeMeetingContext = isMeetingDetailsView && meetingId;

    const sendInsight = (insightName: string, data?: { [key: string]: unknown }) => {
        return trackInsight({
            name: insightName,
            data: {
                ...data,
                organisationId: activeRoom?.organisation?.id,
                organisationName: activeRoom?.organisation?.name,
                roomId: activeRoom?.id,
                userId: member?.user?.id,
                isSherpany: member?.user?.isSherpanyUser,
                language: member?.user?.language,
                roomType: activeRoom?.type,
                userPermission: currentPermission,
                ...(includeMeetingContext || data?.eventId
                    ? {
                          eventId: data?.eventId ?? meetingId,
                          listUserRoleInMeeting: data?.listUserRoleInMeeting ?? loggedMemberRoles
                      }
                    : {})
            },
            state: {
                currentAppSection,
                isAttendeeView,
                activeRoom,
                loggedMember: member?.user,
                currentPermission
            },
            insightsEnabled: APPLICATION_INSIGHTS_ENABLED,
            mixpanelEnabled: MIXPANEL_ENABLED
        });
    };

    return sendInsight;
};

export default useSendInsight;
